import { useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';

import { DayPickerWithField } from 'components/DatePicker';
import FormRow from 'components/FormRow';
import FormSection from 'components/FormSection';
import {
  hausbankFields,
  hausbankFieldsWithSectionString,
  LoanTermInYearsItem,
} from 'hausbank/inquiry/fields';
import { FinancingAmount } from 'hausbank/inquiry/steps/financingNeed/sections/financingNeedSection/components/FinancingAmount';
import { AmountWithField } from 'modules/Inquiry/Form/Steps/FinancingNeed/Amount/Amount';
import { MultiSelectWithField } from 'modules/Inquiry/Form/Steps/RequestDetails/BankSearch/MultiSelect';
import { useInquirySelectOptions } from 'modules/Inquiry/inquiryFieldsTranslations/useInquirySelectValues';
import { translations } from 'new/form/common/types';
import { useTranslations } from 'utils/hooks/useTranslations';

const {
  investmentAmount,
  maintenanceReserve,
  wegContribution,
  constructionSubsidies,
  ownersMeetingDate,
} = hausbankFields.financingNeedPage.financingNeedSection;

export const FinancingNeedSection = () => {
  const t = useTranslations();
  const {
    title,
    fields: {
      loanTermInYears: { caption: loanTermInYearsCaption },
    },
  } = translations.inquiryType.hausbank.pages.financingNeed.sections.financingNeed;
  const loanTermArrayFieldName =
    hausbankFieldsWithSectionString.financingNeedPage.financingNeedSection.loanTermInYears
      .sectionString;
  const { fields } = useFieldArray<LoanTermInYearsItem>(loanTermArrayFieldName);
  const { change } = useForm();
  const selectedOptions = fields.value.map((option) => ({ ...option, key: option.value }));

  const loanTermInYearsOptions = useInquirySelectOptions(loanTermArrayFieldName).map((option) => ({
    ...option,
    key: option.value,
  }));

  const handleChange = (option: (typeof loanTermInYearsOptions)[number]) => {
    const isSelected = fields.value.some((item) => item.value === option.value);
    if (isSelected) {
      const newValue = fields.value.filter((item) => item.value !== option.value);
      return change(loanTermArrayFieldName, newValue);
    }

    return change(loanTermArrayFieldName, [...fields.value, option]);
  };

  return (
    <FormSection title={t(title)} sectionNumber={1}>
      <FormRow>
        <AmountWithField name={investmentAmount} />
      </FormRow>
      <FormRow>
        <AmountWithField name={maintenanceReserve} optional={true} />
      </FormRow>
      <FormRow>
        <AmountWithField name={wegContribution} optional={true} />
      </FormRow>
      <FormRow>
        <AmountWithField name={constructionSubsidies} optional={true} />
      </FormRow>
      <FormRow>
        <FinancingAmount />
      </FormRow>
      <FormRow>
        <MultiSelectWithField
          name={loanTermArrayFieldName}
          options={loanTermInYearsOptions}
          selectedOptions={selectedOptions}
          placeholder={t(translations.placeholders.pleaseChoose)}
          onSelect={handleChange}
          onRemove={handleChange}
          data-testid={'hausbank-loan-term-in-years-field'}
          caption={t(loanTermInYearsCaption)}
          useTagsFormRow={false}
        />
      </FormRow>
      <FormRow>
        <DayPickerWithField name={ownersMeetingDate} />
      </FormRow>
    </FormSection>
  );
};
